import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { Gift } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PRICING, GIFT_OCCASIONS } from "@/data/categories";
import { OccasionPicker } from "@/components/gift/OccasionPicker";
import { RefundAgree } from "@/components/RefundAgree";
import { PayButtons } from "@/components/PayButtons";
import { GiftForm, GiftPreview } from "@/components/gift/GiftForm";
import { GiftList } from "@/components/gift/GiftList";

export default function GiftLifetime() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [cfg, setCfg] = useState(null);
  const initial = GIFT_OCCASIONS.find((o) => o.id === params.get("occasion"));
  const [occasion, setOccasion] = useState(initial?.id || "");
  const [gift, setGift] = useState({ recipient_name: "", from_name: user?.name || "", message: initial?.message || "" });
  const pickOccasion = (o) => {
    const prev = GIFT_OCCASIONS.find((x) => x.id === occasion);
    const untouched = !gift.message.trim() || gift.message === prev?.message;
    setOccasion(o.id);
    if (untouched || window.confirm("Replace your message with the wording for this occasion?")) setGift({ ...gift, message: o.message });
  };
  const [refundOk, setRefundOk] = useState(false);
  const [gifts, setGifts] = useState([]);

  const loadGifts = () => api.get("/gifts/mine").then((r) => setGifts(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/payments/config").then((r) => setCfg(r.data)).catch(() => setCfg({}));
    loadGifts();
  }, []);

  const ready = gift.recipient_name.trim() && gift.from_name.trim();
  const payload = { lookup_key: "gift_lifetime", gift: { ...gift, message: gift.message.trim() || null } };
  const onPaid = (r) => {
    toast.success(`Your gift is ready — code ${r.gift_code}`);
    setGift({ recipient_name: "", from_name: gift.from_name, message: "" });
    setRefundOk(false);
    loadGifts();
  };

  const body = (
    <div data-testid="gift-page" className="mx-auto max-w-5xl px-5 py-8 sm:px-6 sm:py-10">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#E06D53]"><Gift size={16} weight="duotone" /> Give a gift</p>
      <h1 className="mt-2 font-serif text-4xl text-[#2D2824] sm:text-5xl">Give Lifetime Access</h1>
      <p className="mt-3 max-w-2xl leading-relaxed text-[#6E665E]">
        Give someone you love a private place to record their whole life story. You'll receive a gift code and a printable
        certificate to hand over or forward. Their diary stays completely private to them.
      </p>

      <div className="mt-8 grid gap-8 lg:grid-cols-2 [&>*]:min-w-0">
        <div className="space-y-5">
          <OccasionPicker value={occasion} onPick={pickOccasion} />
          <GiftForm value={gift} onChange={setGift} />
          <div className="rounded-2xl bg-white p-5 ring-1 ring-[#EBE4DA]">
            <p className="font-serif text-3xl text-[#2D2824]" data-testid="gift-price">{PRICING.currency}{PRICING.lifetime.price.toFixed(2)}</p>
            <p className="text-xs text-[#8A7F76]">AUD · No GST applies · one-time · code valid for 3 years, one use</p>
            <div className="my-4"><RefundAgree checked={refundOk} onChange={setRefundOk} testId="refund-agree-gift" /></div>
            {ready ? (
              <PayButtons payload={payload} refundOk={refundOk} paypalReady={!!cfg?.client_id} paypalMode={cfg?.mode} cardEnabled={cfg?.card_enabled !== false}
                onPaid={onPaid} cardLabel="Pay with Card · Buy this gift" testPrefix="gift" />
            ) : (
              <p data-testid="gift-fill-names" className="rounded-lg bg-[#FDF7EB] p-3 text-sm text-[#6E665E]">Add the recipient's name and your name to continue.</p>
            )}
          </div>
        </div>
        <GiftPreview value={gift} occasion={GIFT_OCCASIONS.find((o) => o.id === occasion)?.label} />
      </div>

      <GiftList gifts={gifts} highlight={params.get("code")} />
    </div>
  );

  return cfg?.client_id ? (
    <PayPalScriptProvider options={{ clientId: cfg.client_id, currency: "AUD", intent: "capture", components: "buttons", disableFunding: "card" }}>
      {body}
    </PayPalScriptProvider>
  ) : body;
}
